import { NetworkStatus, useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Search } from "@mui/icons-material";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  Box,
  MenuItem,
  SxProps,
  useTheme,
  Typography,
} from "@mui/material";
import { sortBy } from "lodash";
import { ReactElement, useEffect, useMemo } from "react";
import { Control, useController, useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import {
  CheckLinkShortenerDomainNameAvailabilityQuery,
  CheckLinkShortenerDomainNameAvailabilityQueryVariables,
  CreateLinkShortenerDomainRequestMutationVariables,
  LinkShortenerDomainRequestType,
  ListSubaccountsQuery,
  UpdateLinkShortenerDomainMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { RhfSearchableSelect } from "~frontend/components/forms/FormSelect";
import SubmitButton from "~frontend/components/forms/SubmitButton";
import {
  RhfErrorBanner,
  RhfSelect,
  RhfSwitchField,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import { CHECK_LINK_SHORTENER_DOMAIN_AVAILABILITY } from "~frontend/graphql/domains";
import { LIST_SUBACCOUNTS } from "~frontend/graphql/subaccounts";
import {
  useCurrentUserIsStaff,
  useFeatureFlag,
} from "~frontend/providers/LoggedInUserProvider/context";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useDebounce from "~frontend/util/useDebounce";

import {
  AVAILABLE_TLDS,
  DOMAIN_PART_REGEX,
  isValidDomainPart,
} from "./domainRequestValidation";
import { Domain } from "./types";

const schema = z.object({
  newDomainPart: z
    .string()
    .min(1, "Required")
    .regex(DOMAIN_PART_REGEX, "Must be a valid domain name"),
  newDomainTld: z.string(),
  newDomainAvailable: z.boolean(),
  fallbackUrl: z.string().min(1, "Required").url("Must be a valid URL"),
  isSubaccountScoped: z.boolean(),
  assignedSubaccountIds: z.array(z.string()).optional(),
});

type FormValues = z.infer<typeof schema>;

function DomainSearchInput({
  control,
}: {
  control: Control<FormValues>;
}): ReactElement {
  const { field: domainPartField, fieldState: domainPartFieldState } =
    useController({ control, name: "newDomainPart" });
  const newDomainPart = domainPartField.value;
  const newDomainTld = useWatch({ control, name: "newDomainTld" });
  const { field: availableField } = useController({
    control,
    name: "newDomainAvailable",
  });

  const theme = useTheme();
  const debouncedPart = useDebounce(newDomainPart, 500);
  const { data, networkStatus, variables } = useQuery<
    CheckLinkShortenerDomainNameAvailabilityQuery,
    CheckLinkShortenerDomainNameAvailabilityQueryVariables
  >(CHECK_LINK_SHORTENER_DOMAIN_AVAILABILITY, {
    variables: { input: { domain: `${debouncedPart}${newDomainTld}` } },
    notifyOnNetworkStatusChange: true,
    skip: debouncedPart.length < 2 || !isValidDomainPart(debouncedPart),
    fetchPolicy: "network-only",
  });

  let validationStatus:
    | "idle"
    | "loading"
    | "available"
    | "unavailable"
    | "error" = "idle";

  if (
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.setVariables ||
    newDomainPart !== debouncedPart
  ) {
    validationStatus = "loading";
  } else if (networkStatus === NetworkStatus.ready && data) {
    validationStatus = data.checkLinkShortenerDomainNameAvailability
      ? "available"
      : "unavailable";
  } else if (networkStatus === NetworkStatus.error) {
    validationStatus = "error";
  }

  const baseSx: SxProps = { ...theme.typography.body2 };
  const errorSx: SxProps = { ...baseSx, color: theme.palette.error.main };
  const successSx: SxProps = { ...baseSx, color: theme.palette.success.main };

  let availabilityMessage: { message: string; sx: SxProps } = {
    sx: baseSx,
    message: "",
  };
  if (domainPartFieldState.error) {
    availabilityMessage = {
      sx: errorSx,
      message: domainPartFieldState.error.message ?? "",
    };
  } else if (validationStatus === "loading") {
    availabilityMessage = { sx: baseSx, message: "Checking availability..." };
  } else if (validationStatus === "available") {
    availabilityMessage = {
      sx: successSx,
      message: `✔ ${variables?.input.domain} is available!`,
    };
  } else if (validationStatus === "unavailable") {
    availabilityMessage = {
      sx: errorSx,
      message: `${variables?.input.domain} is not available`,
    };
  } else if (validationStatus === "error") {
    availabilityMessage = {
      sx: errorSx,
      message: "Error checking availability",
    };
  }

  useEffect(() => {
    availableField.onChange(validationStatus === "available");
  }, [availableField, validationStatus]);

  return (
    <FlexBox>
      <Box sx={{ flex: 1 }}>
        <RhfTextField
          control={control}
          name="newDomainPart"
          label="New Domain Name"
          fullWidth
          bottomLabel={availabilityMessage.message}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end" sx={{ mx: 1 }}>
                  <Search />
                </InputAdornment>
              ),
            },
            formHelperText: {
              sx: availabilityMessage.sx,
            },
          }}
        />
      </Box>
      <Box sx={{ width: "150px" }}>
        <RhfSelect fullWidth control={control} name="newDomainTld">
          {AVAILABLE_TLDS.map(tld => (
            <MenuItem key={tld} value={tld}>
              {tld}
            </MenuItem>
          ))}
        </RhfSelect>
      </Box>
    </FlexBox>
  );
}

function RhfSubaccountsField({
  control,
  options,
}: {
  control: Control<FormValues>;
  options: { id: string; label: string }[];
}): ReactElement {
  return (
    <RhfSearchableSelect
      control={control}
      name="assignedSubaccountIds"
      label="Subaccounts"
      options={options}
      sx={{
        "& .MuiAutocomplete-inputRoot.MuiInputBase-root": { py: 0 },
      }}
      multiple={true}
    />
  );
}

type RequestOrEditDomainProps = {
  open: boolean;
  onClose: () => void;
} & (
  | {
      onSubmitRequest: (
        values: CreateLinkShortenerDomainRequestMutationVariables
      ) => Promise<void>;
      domain?: never;
      healthCheck?: never;
      onSubmitEdit?: never;
    }
  | {
      domain: Domain;
      onSubmitEdit: (
        values: UpdateLinkShortenerDomainMutationVariables
      ) => Promise<void>;
      healthCheck: ReactElement;
      onSubmitRequest?: never;
    }
);

export default function RequestOrEditDomain({
  open,
  onClose,
  onSubmitRequest,
  onSubmitEdit,
  domain,
  healthCheck,
}: RequestOrEditDomainProps): ReactElement {
  const getErrorMessage = useErrorMessageFormatter();
  const subaccountScopingEnabled = useFeatureFlag(
    "subaccountLinkShortenerDomainScoping"
  );
  const initialValues: FormValues = {
    newDomainPart: domain ? "placeholder" : "",
    newDomainTld: ".com",
    newDomainAvailable: false,
    fallbackUrl: domain?.fallbackUrl ?? "",
    isSubaccountScoped: domain ? !domain.forAllSubaccounts : false,
    assignedSubaccountIds: domain
      ? domain.assignedSubaccounts
          .filter(sa => !sa.deletedAt)
          .map(sa => sa.subaccount.id)
      : [],
  };

  const isStaff = useCurrentUserIsStaff();

  const { control, handleSubmit, watch, reset, setError, formState } =
    useForm<FormValues>({
      resolver: zodResolver(schema),
      defaultValues: initialValues,
    });

  useEffect(() => {
    if (open) {
      reset(initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const newDomainAvailable = watch("newDomainAvailable");
  const isSubaccountScoped = watch("isSubaccountScoped");

  const { data: subaccountsData } = useQuery<ListSubaccountsQuery>(
    LIST_SUBACCOUNTS,
    { skip: !open }
  );

  const subaccountOptions = useMemo(
    () =>
      sortBy(
        subaccountsData?.listSubaccounts.filter(sa => !sa.archivedAt) ?? [],
        "name"
      ).map(sa => ({ id: sa.id, label: sa.name })),
    [subaccountsData?.listSubaccounts]
  );

  function handleClose() {
    onClose();
  }

  async function handleFormSubmit(values: FormValues) {
    try {
      if (domain) {
        await onSubmitEdit({
          input: {
            linkShortenerDomainId: domain.id,
            fallbackUrl: values.fallbackUrl,
            forAllSubaccounts: !values.isSubaccountScoped,
            subaccountIds: values.assignedSubaccountIds,
          },
        });
      } else {
        await onSubmitRequest({
          input: {
            requestType: LinkShortenerDomainRequestType.New,
            domain: `${values.newDomainPart}${values.newDomainTld}`,
            fallbackUrl: values.fallbackUrl,
            forAllSubaccounts: !values.isSubaccountScoped,
            assignedSubaccountIds: values.assignedSubaccountIds,
          },
        });
      }
    } catch (e) {
      setError("root", { message: getErrorMessage(e) });
    }
  }

  return (
    <>
      <DialogTitle>
        {domain
          ? `Edit Your Domain, ${domain.domain}`
          : "Request a Custom Domain"}
      </DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent
          sx={{ display: "flex", gap: 1, flexDirection: "column", pt: 0 }}
        >
          <RhfErrorBanner control={control} />
          <Typography>
            Use the search bar below to look for an available domain. Once you
            have found the domain you want, click the submit button below.{" "}
            <strong>
              Your domain should be available for use within 3 to 5 business
              days.
            </strong>
          </Typography>

          {!domain && <DomainSearchInput control={control} />}

          <Typography>
            Add a redirect URL that your links will default to if broken (this
            is usually your organization&apos;s homepage URL).
          </Typography>
          <RhfTextField
            control={control}
            name="fallbackUrl"
            label="Redirect URL"
            fullWidth
          />

          {subaccountScopingEnabled && (
            <>
              <RhfSwitchField
                control={control}
                name="isSubaccountScoped"
                label="Make available to only select subaccounts"
              />

              {isSubaccountScoped && (
                <>
                  <Typography>
                    Select which subaccounts this domain should be available in.
                  </Typography>
                  <RhfSubaccountsField
                    control={control}
                    options={subaccountOptions}
                  />
                </>
              )}
            </>
          )}
          {isStaff && healthCheck}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            disabled={formState.isSubmitting}
            onClick={handleClose}
            color="secondary"
          >
            Cancel
          </Button>
          <SubmitButton
            isSubmitting={formState.isSubmitting}
            disabled={!formState.isDirty || (!domain && !newDomainAvailable)}
            color="primary"
            variant="contained"
            submitText={domain ? "Save Changes" : "Submit Custom Domain"}
            loadingText="Submitting..."
          />
        </DialogActions>
      </form>
    </>
  );
}
